/**
 * SettingsContext
 * Fetches public settings from /admin/settings/public and makes them
 * available throughout the app without requiring admin auth.
 *
 * Default values are used until the API response arrives, so components
 * render correctly even while loading.
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const SettingsContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Sensible defaults — everything enabled so site works even if DB is empty
const DEFAULTS = {
  payment_stripe_enabled: true,
  payment_paypal_enabled: true,
  payment_venmo_enabled: true,
  payment_wire_transfer_enabled: true,
  shipping_fedex_enabled: true,
  shipping_free_threshold: 0,
  feature_ai_builder_enabled: true,
  feature_ai_builder_login_required: true,
  feature_ai_builder_daily_limit: 10,
  feature_portfolio_enabled: true,
  feature_diamond_search_enabled: true,
  diamonds_show_prices: true,
  diamonds_allow_reserve: true,
  diamonds_price_on_request: false,
  site_maintenance_mode: false,
  site_name: 'SDNY',
  contact_email: '',
  contact_phone: '',
  site_tab_comeback_enabled: false,
  site_tab_comeback_title: '',
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/admin/settings/public`)
      .then((res) => {
        if (!cancelled && res.data?.settings) {
          setSettings({ ...DEFAULTS, ...res.data.settings });
        }
      })
      .catch(() => {
        // Keep defaults on error — site still works
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Helper: get a single setting by key with optional fallback.
   * e.g.  getSetting('payment_wire_transfer_enabled', true)
   */
  const getSetting = (key, fallback = undefined) => {
    const val = settings[key];
    return val !== undefined ? val : (fallback !== undefined ? fallback : DEFAULTS[key]);
  };

  return (
    <SettingsContext.Provider value={{ settings, loading, getSetting }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    // Return defaults gracefully when used outside provider
    return { settings: DEFAULTS, loading: false, getSetting: (k, fb) => DEFAULTS[k] ?? fb };
  }
  return ctx;
}
